import { TranslateFunc } from "utils/translation.utils";
import { IGroup } from "domain/group/group";
import { GroupType } from "../group/group-type";
import { IPaginationMeta } from "../pagination/pagination-meta";
import { Language } from "./language";
import { getRolePomTranslation, RolePom } from "./role-pom";

export interface IUser {
  active: boolean;
  deliveryEmail: string;
  email: string;
  /** Email of the portal login, the login-as target; may differ from `email`. */
  username: string;
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  language: Language;
  telephone: string;
  title: string;
  group?: IGroup;
  externalId: string;
  notify?: boolean;
  rolePom?: RolePom;
  /** Only set by user lists; `undefined` means unknown. */
  hasLogin?: boolean;
}

export interface IUserLoginAs {
  firstName: string | undefined;
  lastName: string | undefined;
  username: string;
}

export interface IUserResult {
  users: IUser[];
  meta?: IPaginationMeta;
}

export function userHasType(user: IUser | undefined, type: GroupType | undefined): boolean {
  if (!user || !type) {
    return false;
  }

  return user.group?.type === type;
}

// Shared
export function isAdmin(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.Admin);
}

export function isCustomer(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.Customer);
}

// POM
export function isPom(user: IUser | undefined): boolean {
  return isAdmin(user) || isPomUser(user) || isPomRepresentative(user);
}

export function isPomAgency(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.PomAgency);
}

export function isPomUser(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.Customer) && (user?.group?.pomContracts?.length ?? 0) > 0;
}

export function isPomRepresentative(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.PomRepresentative);
}

export function isPomAgencyMember(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.PomAgencyMember);
}

// roles
export function isPomMainContact(user: IUser | undefined): boolean {
  return (isPomUser(user) || isPomAgency(user) || isPomRepresentative(user)) && user?.rolePom === RolePom.MainContact;
}

export function canAccessReporting(user: IUser | undefined): boolean {
  return isCustomer(user) || isPomRepresentative(user) || isPomAgency(user) || isPomAgencyMember(user);
}

// misc
export const getGroupName = (user: IUser): string => {
  return user.group?.name ?? "";
};

export const getFullName = (user: IUser): string => {
  return `${user.firstName ?? ""} ${user.lastName ?? ""}`;
};

export const getPomRoleName = (user: IUser, translate: TranslateFunc): string => {
  if (!user.rolePom) return "";
  return translate(getRolePomTranslation(user.rolePom));
};
